import { DataGrid } from '@mui/x-data-grid'
import { Box, IconButton, Button, Tooltip, Chip, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import TextField from '@mui/material/TextField'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { getAllExploreRows } from '../../graphql/queries'
import { useState, useEffect, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExploreIcon from '@mui/icons-material/Explore'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import ExploreRowEditModal from '../ExploreRowEditModal'

const columns = [
  {
    field: 'tableIndex',
    headerName: '#',
    filterable: false,
    minWidth: 120,
    maxWidth: 170,
    flex: 1,
    sortable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
        {params.value}
        <DetailPage id={params.row.id} onOpenModal={params.row.onOpenModal} />
      </Box>
    ),
  },
  {
    field: 'order',
    headerName: 'Order',
    sortable: true,
    minWidth: 100,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', fontWeight: 'bold' }}>
        #{params.value}
      </Box>
    ),
  },
  {
    field: 'isHighlighted',
    headerName: 'Highlighted',
    sortable: false,
    minWidth: 120,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {params.value ? (
          <StarIcon sx={{ color: '#ffd700' }} />
        ) : (
          <StarBorderIcon sx={{ color: '#gray' }} />
        )}
        {params.value ? 'Yes' : 'No'}
      </Box>
    ),
  },
  {
    field: 'contentType',
    headerName: 'Content Type',
    sortable: false,
    minWidth: 130,
    flex: 1,
    renderCell: params => {
      const getChipColor = (type) => {
        switch (type) {
          case 'PROMPT': return 'primary'
          case 'STYLE': return 'secondary'
          case 'SECTION': return 'success'
          default: return 'default'
        }
      }
      return (
        <Chip 
          label={params.value} 
          color={getChipColor(params.value)}
          size="small"
          variant="outlined"
        />
      )
    },
  },
  {
    field: 'platform',
    headerName: 'Platform',
    sortable: false,
    minWidth: 140,
    flex: 1,
    renderCell: params => {
      const platforms = params.value || []
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {platforms.includes('IOS') && (
            <Chip
              icon={<AppleIcon />}
              label="iOS"
              size="small"
              sx={{
                backgroundColor: '#000',
                color: '#fff',
                '& .MuiChip-icon': { color: '#fff' }
              }}
            />
          )}
          {platforms.includes('ANDROID') && (
            <Chip
              icon={<AndroidIcon />}
              label="Android"
              size="small"
              sx={{
                backgroundColor: '#3DDC84',
                color: '#000',
                '& .MuiChip-icon': { color: '#000' }
              }}
            />
          )}
          {platforms.length === 0 && (
            <span style={{ color: '#999', fontStyle: 'italic' }}>No platform</span>
          )}
        </Box>
      )
    },
  },
  {
    field: 'contentId',
    headerName: 'Content ID',
    sortable: false,
    minWidth: 220,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box sx={{ 
          maxWidth: '180px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {params.value}
        </Box>
        <CopyCell value={params.value} />
      </Box>
    ),
  },
  {
    field: 'tagId',
    headerName: 'Tag ID',
    sortable: false,
    minWidth: 200,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {params.value ? (
          <>
            <Box sx={{ 
              maxWidth: '160px', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {params.value}
            </Box>
            <CopyCell value={params.value} />
          </>
        ) : (
          <span style={{ color: '#999', fontStyle: 'italic' }}>No tag</span>
        )}
      </Box>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    sortable: false,
    minWidth: 120,
    flex: 1,
    valueGetter: params => new Date(params.row.createdAt).toLocaleDateString('tr-TR'),
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    sortable: false,
    minWidth: 120,
    flex: 1,
    valueGetter: params => new Date(params.row.updatedAt).toLocaleDateString('tr-TR'),
  },
  {
    field: 'id',
    headerName: 'Row ID',
    sortable: false,
    minWidth: 220,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Box sx={{ 
          maxWidth: '180px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {params.value}
        </Box>
        <CopyCell value={params.value} />
      </Box>
    ),
  },
]

export function DetailPage({ id, onOpenModal }) {
  return (
    <Tooltip title="View ExploreRow Details">
      <Button
        onClick={() => onOpenModal(id)}
        variant="outlined"
        className="actionBtn"
        sx={{ fontSize: {xs:'0.7rem', md:'small'} }}
      >
        Details
      </Button>
    </Tooltip>
  )
}

export function CopyCell({ value }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy}>
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  )
}

export default function ExploreRowTable({ platformFilter: parentPlatformFilter }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSearch = searchParams.get('search') || ''
  const initialContentType = searchParams.get('contentType') || ''
  const initialHighlighted = searchParams.get('highlighted') || ''
  const initialPlatform = searchParams.get('platform') || ''
  const [search, setSearch] = useState(initialSearch)
  const [contentTypeFilter, setContentTypeFilter] = useState(initialContentType)
  const [highlightedFilter, setHighlightedFilter] = useState(initialHighlighted)
  const [platformFilter, setPlatformFilter] = useState(initialPlatform)
  const searchRef = useRef(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedRowId, setSelectedRowId] = useState(null)
  
  // Use parent platform filter if provided, otherwise use local state
  const activePlatformFilter = parentPlatformFilter !== undefined ? parentPlatformFilter : platformFilter

  const { loading, error, data } = useQuery(getAllExploreRows, {
    fetchPolicy: 'network-only'
  })

  const handleOpenModal = (id) => {
    setSelectedRowId(id)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedRowId(null)
  }

  useEffect(() => {
    const params = {}
    if (search) params.search = search
    if (contentTypeFilter) params.contentType = contentTypeFilter
    if (highlightedFilter) params.highlighted = highlightedFilter
    if (platformFilter) params.platform = platformFilter
    setSearchParams(params)
  }, [search, contentTypeFilter, highlightedFilter, platformFilter, setSearchParams])

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
  }

  const handleClearSearch = () => {
    setSearch('')
    if (searchRef.current) {
      searchRef.current.focus()
    }
  }

  const handleContentTypeChange = (event) => {
    setContentTypeFilter(event.target.value)
  }

  const handleHighlightedChange = (event) => {
    setHighlightedFilter(event.target.value)
  }

  const handlePlatformChange = (event) => {
    setPlatformFilter(event.target.value)
  }

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error.message}</div>

  const exploreRows = data?.getAllExploreRows || []
  
  // Debug: Log first row's platform data
  if (exploreRows.length > 0) {
    console.log('First explore row:', exploreRows[0])
    console.log('Platform data:', exploreRows[0].platform)
  }
  
  // Filter explore rows based on search and filters
  let filteredRows = exploreRows

  if (search) {
    filteredRows = filteredRows.filter(row =>
      row.contentId.toLowerCase().includes(search.toLowerCase()) ||
      row.id.toLowerCase().includes(search.toLowerCase()) ||
      (row.tagId && row.tagId.toLowerCase().includes(search.toLowerCase()))
    )
  }

  if (contentTypeFilter) {
    filteredRows = filteredRows.filter(row => row.contentType === contentTypeFilter)
  }

  if (highlightedFilter) {
    const isHighlighted = highlightedFilter === 'true'
    filteredRows = filteredRows.filter(row => row.isHighlighted === isHighlighted)
  }

  if (activePlatformFilter) {
    filteredRows = filteredRows.filter(row => {
      const platforms = row.platform || []
      return platforms.includes(activePlatformFilter)
    })
  }

  // Sort by order field (create a copy to avoid mutating read-only array)
  filteredRows = [...filteredRows].sort((a, b) => a.order - b.order)

  const rowsData = filteredRows.map((row, index) => ({
    ...row,
    tableIndex: index + 1,
    onOpenModal: handleOpenModal,
  }))

  return (
    <Box sx={{ width: '100%' }}>
      {/* Filters */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 2, 
        flexWrap: 'wrap',
        alignItems: 'center' 
      }}>
        {/* Search */}
        <TextField
          ref={searchRef}
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search by content ID, row ID, or tag ID..."
          sx={{ minWidth: 250 }}
          InputProps={{
            endAdornment: search && (
              <IconButton size="small" onClick={handleClearSearch}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />

        {/* Content Type Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Content Type</InputLabel>
          <Select
            value={contentTypeFilter}
            label="Content Type"
            onChange={handleContentTypeChange}
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="PROMPT">Prompt</MenuItem>
            <MenuItem value="STYLE">Style</MenuItem>
            <MenuItem value="SECTION">Section</MenuItem>
          </Select>
        </FormControl>

        {/* Highlighted Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Highlighted</InputLabel>
          <Select
            value={highlightedFilter}
            label="Highlighted"
            onChange={handleHighlightedChange}
          >
            <MenuItem value="">All Items</MenuItem>
            <MenuItem value="true">Highlighted Only</MenuItem>
            <MenuItem value="false">Not Highlighted</MenuItem>
          </Select>
        </FormControl>

        {/* Platform Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Platform</InputLabel>
          <Select
            value={platformFilter}
            label="Platform"
            onChange={handlePlatformChange}
          >
            <MenuItem value="">All Platforms</MenuItem>
            <MenuItem value="IOS">iOS Only</MenuItem>
            <MenuItem value="ANDROID">Android Only</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Data Grid */}
      <DataGrid
        rows={rowsData}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f5f5f5',
          },
        }}
      />

      {/* Edit Modal */}
      <ExploreRowEditModal
        open={modalOpen}
        onClose={handleCloseModal}
        exploreRowId={selectedRowId}
      />
    </Box>
  )
}
